import { CalculatorInputs, PricingConfig, CalculationResults, WorkflowSummary, AccessNowPricing } from '@/types/calculator';
import { CALCULATION_CONSTANTS } from '@/lib/env';

const { HOURS_PER_YEAR, MINUTES_PER_HOUR, WORKFLOW_INCLUDED_REQUESTS, SMALL_REQUEST_PACKAGE_SIZE, LARGE_REQUEST_PACKAGE_SIZE } = CALCULATION_CONSTANTS;

function calculateAccessNowPricing(
  credentialRequestsPerYear: number,
  accessRequestsPerYear: number,
  visitorRequestsPerYear: number,
  differentiateEmployeeContractor: boolean,
  pricing: PricingConfig
): AccessNowPricing {
  // Calculate total requests needed first
  const totalRequestsNeeded = credentialRequestsPerYear + accessRequestsPerYear + visitorRequestsPerYear;
  
  // Count required workflows
  let totalWorkflowsNeeded = 0;
  const workflowCounts = {
    credential: 0,
    access: 0,
    visitor: 0,
  };

  // Count workflows needed
  if (credentialRequestsPerYear > 0) {
    workflowCounts.credential = differentiateEmployeeContractor ? 2 : 1;
    totalWorkflowsNeeded += workflowCounts.credential;
  }

  if (accessRequestsPerYear > 0) {
    workflowCounts.access = differentiateEmployeeContractor ? 2 : 1;
    totalWorkflowsNeeded += workflowCounts.access;
  }

  if (visitorRequestsPerYear > 0) {
    workflowCounts.visitor = 1;
    totalWorkflowsNeeded += workflowCounts.visitor;
  }

  // Handle case where no requests are needed at all
  if (totalRequestsNeeded === 0) {
    return {
      workflows: 0,
      extraRequestsSmall: 0,
      extraRequestsLarge: 0,
      totalCost: 0,
      workflowCounts,
      requestsBreakdown: {
        includedRequests: 0,
        additionalRequests: 0,
        totalRequests: 0,
      },
      costBreakdown: {
        workflowsCost: 0,
        extraRequestsCost: 0,
      },
    };
  }

  // Calculate included requests from workflows (1000 per workflow)
  const includedRequestsFromWorkflows = totalWorkflowsNeeded * WORKFLOW_INCLUDED_REQUESTS;
  
  // Calculate additional requests beyond workflow included requests
  const additionalRequestsNeeded = Math.max(0, totalRequestsNeeded - includedRequestsFromWorkflows);
  
  // Optimize request packages - prioritize large packages when cost-effective
  let extraRequestsLarge = 0;
  let extraRequestsSmall = 0;
  
  if (additionalRequestsNeeded > 0) {
    // Calculate cost per request for each package type
    const costPerRequestSmall = pricing.extraRequestsSmallPrice / SMALL_REQUEST_PACKAGE_SIZE;
    const costPerRequestLarge = pricing.extraRequestsLargePrice / LARGE_REQUEST_PACKAGE_SIZE;
    
    // Use large packages when they're more cost-effective
    if (costPerRequestLarge <= costPerRequestSmall) {
      extraRequestsLarge = Math.floor(additionalRequestsNeeded / LARGE_REQUEST_PACKAGE_SIZE);
      const remainingRequests = additionalRequestsNeeded % LARGE_REQUEST_PACKAGE_SIZE;
      extraRequestsSmall = Math.ceil(remainingRequests / SMALL_REQUEST_PACKAGE_SIZE);
    } else {
      // Only use small packages
      extraRequestsSmall = Math.ceil(additionalRequestsNeeded / SMALL_REQUEST_PACKAGE_SIZE);
    }
  }

  // Calculate costs
  const workflowsCost = totalWorkflowsNeeded * pricing.workflowPrice;
  const extraRequestsCost = 
    (extraRequestsSmall * pricing.extraRequestsSmallPrice) + 
    (extraRequestsLarge * pricing.extraRequestsLargePrice);
  
  const totalCost = workflowsCost + extraRequestsCost;
  
  // Calculate total included requests
  const includedRequests = includedRequestsFromWorkflows + 
    (extraRequestsSmall * SMALL_REQUEST_PACKAGE_SIZE) + 
    (extraRequestsLarge * LARGE_REQUEST_PACKAGE_SIZE);

  return {
    workflows: totalWorkflowsNeeded,
    extraRequestsSmall,
    extraRequestsLarge,
    totalCost,
    workflowCounts,
    requestsBreakdown: {
      includedRequests,
      additionalRequests: additionalRequestsNeeded,
      totalRequests: totalRequestsNeeded,
    },
    costBreakdown: {
      workflowsCost,
      extraRequestsCost,
    },
  };
}

export function calculateROI(
  inputs: CalculatorInputs,
  pricing: PricingConfig
): CalculationResults {
  // Use monthly input if provided (Simple calculator), otherwise calculate from database
  // Only calculate if the workflow is enabled (useCredentialRequests toggle)
  const credentialRequestsPerYear = inputs.useCredentialRequests === false ? 0 : (
    inputs.credentialRequestsPerMonth > 0
      ? inputs.credentialRequestsPerMonth * 12
      : (() => {
          const cardReplacementsPerYear = (inputs.cardholderDatabaseSize * inputs.cardReplacementPercentage) / 100;
          const newHiresPerYear = (inputs.cardholderDatabaseSize * inputs.staffTurnoverRate) / 100;
          const staffGrowthPerYear = (inputs.cardholderDatabaseSize * inputs.staffGrowthRate) / 100;
          return cardReplacementsPerYear + newHiresPerYear + staffGrowthPerYear;
        })()
  );
  
  // Prioritize database-driven calculation when cardholder data is available
  // Only use monthly input for Simple calculator mode when no database size is provided
  // Only calculate if the workflow is enabled (useAccessRequests toggle)
  const accessRequestsPerYear = inputs.useAccessRequests === false ? 0 : (
    inputs.cardholderDatabaseSize > 0
      ? inputs.cardholderDatabaseSize * inputs.accessRequestsPerCardholder
      : inputs.accessRequestsPerMonth * 12
  );
  
  // Only calculate if the workflow is enabled (useVisitorRequests toggle)
  const visitorRequestsPerYear = (inputs.useVisitorRequests === false || !inputs.hasVisitorRegistration)
    ? 0 
    : inputs.visitorsPerMonth * 12;
  
  // Calculate base requests (for operational cost calculations)
  const baseRequestsPerYear = credentialRequestsPerYear + accessRequestsPerYear + visitorRequestsPerYear;
  
  // Apply extra request capacity buffer (for licensing calculations only)
  const licensingRequestsPerYear = Math.round(baseRequestsPerYear * (1 + inputs.extraRequestCapacityPercentage / 100));
  
  // For backwards compatibility and display purposes
  const totalRequestsPerYear = licensingRequestsPerYear;
  
  // Calculate manual hours and costs
  const credentialManualHoursPerYear = (credentialRequestsPerYear * inputs.timePerRequest) / MINUTES_PER_HOUR;
  const accessManualHoursPerYear = (accessRequestsPerYear * inputs.timePerRequest) / MINUTES_PER_HOUR;
  const visitorManualHoursPerYear = (visitorRequestsPerYear * inputs.timePerVisitor) / MINUTES_PER_HOUR;
  
  const credentialAccessNowHoursPerYear = (credentialRequestsPerYear * inputs.accessNowTimePerRequest) / MINUTES_PER_HOUR;
  const accessAccessNowHoursPerYear = (accessRequestsPerYear * inputs.accessNowTimePerRequest) / MINUTES_PER_HOUR;
  const visitorAccessNowHoursPerYear = (visitorRequestsPerYear * inputs.accessNowTimePerVisitor) / MINUTES_PER_HOUR;
  
  const credentialHoursSaved = credentialManualHoursPerYear - credentialAccessNowHoursPerYear;
  const accessHoursSaved = accessManualHoursPerYear - accessAccessNowHoursPerYear;
  const visitorHoursSaved = visitorManualHoursPerYear - visitorAccessNowHoursPerYear;
  
  // Calculate individual hourly rate
  const individualHourlyRate = inputs.costPerEmployee / HOURS_PER_YEAR;
  
  // Calculate manual costs using work-based approach
  const credentialManualCost = credentialManualHoursPerYear * individualHourlyRate;
  const accessManualCost = accessManualHoursPerYear * individualHourlyRate;
  const visitorManualCost = visitorManualHoursPerYear * individualHourlyRate;
  
  // Calculate cost savings based on hours saved
  const credentialCostSavings = credentialHoursSaved * individualHourlyRate;
  const accessCostSavings = accessHoursSaved * individualHourlyRate;
  const visitorCostSavings = visitorHoursSaved * individualHourlyRate;
  
  const totalManualCost = credentialManualCost + accessManualCost + visitorManualCost;
  
  // Calculate pricing - use requests with capacity buffer for licensing
  const credentialRequestsWithCapacity = Math.round(credentialRequestsPerYear * (1 + inputs.extraRequestCapacityPercentage / 100));
  const accessRequestsWithCapacity = Math.round(accessRequestsPerYear * (1 + inputs.extraRequestCapacityPercentage / 100));
  const visitorRequestsWithCapacity = Math.round(visitorRequestsPerYear * (1 + inputs.extraRequestCapacityPercentage / 100));
  
  const accessNowPricing = calculateAccessNowPricing(
    credentialRequestsWithCapacity,
    accessRequestsWithCapacity,
    visitorRequestsWithCapacity,
    inputs.differentiateEmployeeContractor,
    pricing
  );
  
  const totalAutomationCost = accessNowPricing.totalCost;
  
  const totalCostSavings = credentialCostSavings + accessCostSavings + visitorCostSavings;
  const totalSavings = totalCostSavings - totalAutomationCost; // Allow negative values for accurate ROI analysis
  
  // Calculate time savings and FTE reduction
  const rawCredentialHoursSaved = credentialHoursSaved;
  const rawAccessHoursSaved = accessHoursSaved;
  const rawVisitorHoursSaved = visitorHoursSaved;
  
  const totalRawHoursSaved = rawCredentialHoursSaved + rawAccessHoursSaved + rawVisitorHoursSaved;
  
  // Calculate total manual and AccessNow hours
  const totalManualHours = Math.round(credentialManualHoursPerYear + accessManualHoursPerYear + visitorManualHoursPerYear);
  const totalAccessNowHours = Math.round(credentialAccessNowHoursPerYear + accessAccessNowHoursPerYear + visitorAccessNowHoursPerYear);
  
  const credentialFTEReduction = credentialHoursSaved / HOURS_PER_YEAR;
  const accessFTEReduction = accessHoursSaved / HOURS_PER_YEAR;
  const visitorFTEReduction = visitorHoursSaved / HOURS_PER_YEAR;
  
  const fteReduction = credentialFTEReduction + accessFTEReduction + visitorFTEReduction;
  
  // Ensure totalHoursSaved always equals totalManualHours - totalAccessNowHours (for consistency)
  const totalHoursSaved = totalManualHours - totalAccessNowHours;
  
  const rawBusinessEfficiencyValue = (totalRawHoursSaved && individualHourlyRate && !isNaN(totalRawHoursSaved) && !isNaN(individualHourlyRate)) 
    ? totalRawHoursSaved * individualHourlyRate 
    : 0;
  
  const businessEfficiencyValue = Math.min(rawBusinessEfficiencyValue, totalManualCost);
  
  // Create workflow summary
  const workflowSummary: WorkflowSummary[] = [];
  
  if (credentialRequestsPerYear > 0) {
    workflowSummary.push({
      name: 'Credential Management',
      requestsPerYear: Math.round(credentialRequestsPerYear),
      hoursPerYear: Math.round(credentialManualHoursPerYear),
      hoursSavedPerYear: Math.round(credentialHoursSaved),
      savingsPerYear: Math.round(credentialCostSavings),
      manualLaborCost: Math.round(credentialManualCost),
    });
  }
  
  if (accessRequestsPerYear > 0) {
    workflowSummary.push({
      name: 'Access Requests',
      requestsPerYear: Math.round(accessRequestsPerYear),
      hoursPerYear: Math.round(accessManualHoursPerYear),
      hoursSavedPerYear: Math.round(accessHoursSaved),
      savingsPerYear: Math.round(accessCostSavings),
      manualLaborCost: Math.round(accessManualCost),
    });
  }
  
  if (inputs.hasVisitorRegistration && visitorRequestsPerYear > 0) {
    workflowSummary.push({
      name: 'Visitor Registration',
      requestsPerYear: Math.round(visitorRequestsPerYear),
      hoursPerYear: Math.round(visitorManualHoursPerYear),
      hoursSavedPerYear: Math.round(visitorHoursSaved),
      savingsPerYear: Math.round(visitorCostSavings),
      manualLaborCost: Math.round(visitorManualCost),
    });
  }
  
  // Calculate capacity validation - include ALL workflows for consistency with ROI Summary
  const totalAllWorkflowHours = credentialManualHoursPerYear + accessManualHoursPerYear + visitorManualHoursPerYear;
  const totalAvailableHours = inputs.cardholderAdmins * HOURS_PER_YEAR;
  const actualCapacityRatio = totalAvailableHours > 0 ? totalAllWorkflowHours / totalAvailableHours : 0;
  const isOverCapacity = actualCapacityRatio > 1;
  const additionalStaffNeeded = isOverCapacity 
    ? Math.ceil((totalAllWorkflowHours - totalAvailableHours) / HOURS_PER_YEAR)
    : undefined;

  // Calculate AccessNow capacity requirements - include ALL workflows
  const totalAccessNowAllWorkflowHours = credentialAccessNowHoursPerYear + accessAccessNowHoursPerYear + visitorAccessNowHoursPerYear;
  const accessNowCapacityRatio = totalAvailableHours > 0 ? totalAccessNowAllWorkflowHours / totalAvailableHours : 0;
  const accessNowIsOverCapacity = accessNowCapacityRatio > 1;
  const accessNowAdditionalStaffNeeded = accessNowIsOverCapacity 
    ? Math.ceil((totalAccessNowAllWorkflowHours - totalAvailableHours) / HOURS_PER_YEAR)
    : undefined;

  const capacityValidation = {
    allocatedHoursPerYear: Math.round(totalAvailableHours),
    requiredHoursPerYear: Math.round(totalAllWorkflowHours),
    capacityRatio: parseFloat(actualCapacityRatio.toFixed(2)),
    isOverCapacity,
    additionalStaffNeeded,
    // AccessNow capacity calculations
    accessNowRequiredHoursPerYear: Math.round(totalAccessNowAllWorkflowHours),
    accessNowCapacityRatio: parseFloat(accessNowCapacityRatio.toFixed(2)),
    accessNowIsOverCapacity,
    accessNowAdditionalStaffNeeded,
  };
  
  return {
    totalSavings: Math.round(totalSavings), // Year 1 net cash flow (can be negative)
    totalManualCost: Math.round(totalManualCost), // This is the actual manual labor cost (from base requests)
    totalManualHours, // Total manual hours required (from base requests)
    totalAccessNowHours, // Total AccessNow hours required (from base requests)
    businessEfficiencyValue: Math.round(businessEfficiencyValue), // Dollar value of hours returned to business
    fteReduction: parseFloat(fteReduction.toFixed(2)),
    totalHoursSaved,
    credentialWorkflowSavings: Math.round(credentialCostSavings),
    accessWorkflowSavings: Math.round(accessCostSavings),
    visitorWorkflowSavings: Math.round(visitorCostSavings),
    totalRequestsPerYear: Math.round(totalRequestsPerYear), // This is licensing requests (with capacity buffer)
    baseRequestsPerYear: Math.round(baseRequestsPerYear), // Base operational requests (without capacity buffer)
    workflowSummary,
    accessNowPricing,
    capacityValidation,
  };
}
